import { Link } from "react-router";

import { BotonDescargarCotizacion } from "@/components/informes/BotonDescargarCotizacion";
import { Button } from "@/components/ui/button";
import type { Informe } from "@/lib/informes";

import { puedeCotizar } from "./acciones";
import { describirEntrega, formatearFecha } from "@/lib/fechas";

/**
 * El banner de la pantalla de edición que lleva a cotizar (Módulo F).
 *
 * Tiene tres caras porque son tres situaciones distintas del proceso, no tres
 * estilos del mismo aviso:
 *
 * 1. **Todavía no se cotizó** → invita a hacerlo.
 * 2. **Ya se cotizó** → muestra el precio y la entrega comprometidos, y ofrece
 *    regenerar (RF-G2 permite corregir mientras está en revisión).
 * 3. **El estado no admite cotizar** → no se dibuja nada. Un banner apagado
 *    sin motivo se lee como un error de la aplicación.
 *
 * La regla de qué estados cotizan vive en `acciones.ts`, compartida con el
 * listado: dos copias se desincronizan en cuanto se agregue un estado.
 */
export function BannerDeCotizacion({ informe }: { informe: Informe }) {
  const cotizacion = informe.cotizacion ?? null;

  if (!puedeCotizar(informe.estado)) return null;

  return (
    <section className="bg-ink flex flex-wrap items-center justify-between gap-4 rounded-xl px-6 py-5 shadow-sm">
      <div className="space-y-1">
        <p className="text-xs font-bold tracking-widest text-white/60">
          {cotizacion ? "COTIZACIÓN ENVIADA A REVISIÓN" : "SIGUIENTE PASO DEL PROCESO"}
        </p>

        {cotizacion ? (
          <p className="text-sm text-white">
            <span className="font-mono font-bold">{cotizacion.precio_uf} UF</span>
            {" · entrega "}
            <span className="font-mono">{describirEntrega(cotizacion.fecha_entrega, cotizacion.plazo_dias_habiles)}</span>
            {" · versión "}
            <span className="font-mono">{cotizacion.version}</span>
          </p>
        ) : (
          <p className="text-sm text-white">
            Valoriza el informe y envía la cotización a revisión.
          </p>
        )}

        {/* La agenda INTERNA, en una segunda línea y más apagada: no es lo que
            se le ofreció al cliente —eso es el precio y la entrega de arriba—
            sino cómo lo planificó el equipo. Se dibuja solo si hay al menos una
            de las dos; con ninguna, la línea sería un rótulo vacío. */}
        {cotizacion?.fecha_visita_estimada || cotizacion?.fecha_entrega_estimada ? (
          <p className="text-xs text-white/60">
            Interno · visita{" "}
            <span className="font-mono">
              {cotizacion.fecha_visita_estimada
                ? formatearFecha(cotizacion.fecha_visita_estimada)
                : "sin agendar"}
            </span>
            {" · entrega estimada "}
            <span className="font-mono">
              {cotizacion.fecha_entrega_estimada
                ? formatearFecha(cotizacion.fecha_entrega_estimada)
                : "sin estimar"}
            </span>
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {/* El documento se DESCARGA desde acá, no se mira: lo que el equipo
            necesita es el archivo —para mandarlo, imprimirlo o guardarlo— y un
            visor dentro de la aplicación era un paso de más.

            Va desde el banner y no solo desde la confirmación que aparece
            justo después de cotizar: esa pantalla es de un instante, y la
            cotización se consulta durante todos los días que espera visado. */}
        {cotizacion ? (
          <BotonDescargarCotizacion informeId={informe.id} variant="secondary">
            Descargar cotización
          </BotonDescargarCotizacion>
        ) : null}

        <Button asChild variant="secondary" className="bg-white text-ink hover:bg-white/90">
          <Link to={`/informes/${informe.id}/cotizacion`}>
            {cotizacion ? "Regenerar cotización" : "Cotizar"}
          </Link>
        </Button>
      </div>
    </section>
  );
}
